import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaFacebook, FaInstagram, FaWhatsapp, FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaArrowRight } from 'react-icons/fa';

const Footer = () => {
    const currentYear = new Date().getFullYear();

    const quickLinks = [
        { name: 'Home', href: '/' },
        { name: 'Services', href: '/services' },
        { name: 'About Us', href: '/about' },
        { name: 'Contact', href: '/contact' },
    ];

    const services = [
        { name: 'Daily Car Cleaning', href: '/services#car-cleaning' },
        { name: 'Steam Ironing', href: '/services#ironing' },
    ];

    return (
        <footer className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0d3b4c 0%, #0a2f3d 100%)' }}>
            {/* Decorative gradient line */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500 to-transparent"></div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                    {/* Brand Column */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Link to="/">
                            <img
                                src="/images/logo.png"
                                alt="Goprezz Logo"
                                className="h-16 w-auto mb-6"
                            />
                        </Link>
                        <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            Premium doorstep services for daily car cleaning, steam ironing, and more. Making everyday life easier, smoother, and stress-free.
                        </p>
                        <div className="flex gap-4">
                            <a
                                href="https://www.facebook.com/share/17oPmVW7PS/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-primary-500 transition-all duration-300"
                                style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.7)' }}
                            >
                                <FaFacebook className="w-5 h-5" />
                            </a>
                            <a
                                href="https://www.instagram.com/goprezzpvt"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 transition-all duration-300"
                                style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.7)' }}
                            >
                                <FaInstagram className="w-5 h-5" />
                            </a>
                            <a
                                href="https://wa.me/919059651065"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-green-500 transition-all duration-300"
                                style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.7)' }}
                            >
                                <FaWhatsapp className="w-5 h-5" />
                            </a>
                        </div>
                    </motion.div>

                    {/* Quick Links */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        <h3 className="font-semibold text-lg mb-6" style={{ color: '#ffffff' }}>Quick Links</h3>
                        <ul className="space-y-3">
                            {quickLinks.map((link) => (
                                <li key={link.name}>
                                    <Link
                                        to={link.href}
                                        className="hover:text-primary-400 transition-colors duration-200 text-sm flex items-center gap-2 group"
                                        style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                                    >
                                        <FaArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Services */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <h3 className="font-semibold text-lg mb-6" style={{ color: '#ffffff' }}>Our Services</h3>
                        <ul className="space-y-3">
                            {services.map((service) => (
                                <li key={service.name}>
                                    <Link
                                        to={service.href}
                                        className="hover:text-primary-400 transition-colors duration-200 text-sm flex items-center gap-2 group"
                                        style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                                    >
                                        <FaArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
                                        {service.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Contact Info */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                    >
                        <h3 className="font-semibold text-lg mb-6" style={{ color: '#ffffff' }}>Contact Us</h3>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3">
                                <FaPhone className="w-4 h-4 text-primary-400 mt-1" />
                                <a href="tel:+919059651065" className="text-sm transition-colors hover:opacity-100" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                    +91 90596 51065
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <FaEnvelope className="w-4 h-4 text-primary-400 mt-1" />
                                <a href="mailto:goprezzpvt@gmail.com" className="text-sm transition-colors hover:opacity-100" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                    goprezzpvt@gmail.com
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <FaMapMarkerAlt className="w-4 h-4 text-primary-400 mt-1" />
                                <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Hyderabad, Telangana</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <FaClock className="w-4 h-4 text-primary-400 mt-1" />
                                <div className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                    <p>Mon - Sat: 9 AM - 6 PM</p>
                                    <p>Sunday: Holiday</p>
                                </div>
                            </li>
                        </ul>
                    </motion.div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-12 pt-8" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                            © {currentYear} Goprezz Pvt. Ltd. All rights reserved.
                        </p>
                        <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                            Premium services at your doorstep
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
